import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/** Layout presets */
const VARIANTS = {
  grid: {
    cols: 2,
    rows: 2,
    areas: [
      ["a", "b"],
      ["c", "d"],
    ],
  },
  "spotlight-left": {
    cols: 2,
    rows: 3,
    areas: [
      ["a", "b"],
      ["a", "c"],
      ["a", "d"],
    ],
  },
  "spotlight-right": {
    cols: 2,
    rows: 3,
    areas: [
      ["b", "a"],
      ["c", "a"],
      ["d", "a"],
    ],
  },
  magazine: {
    cols: 3,
    rows: 2,
    areas: [
      ["a", "a", "a"],
      ["b", "c", "d"],
    ],
  },
  diamond: {
    cols: 3,
    rows: 2,
    areas: [
      ["b", "a", "c"],
      ["b", "d", "c"],
    ],
  },
  mosaic: {
    cols: 3,
    rows: 2,
    areas: [
      ["a", "b", "b"],
      ["a", "c", "d"],
    ],
  },
};

const TILE_KEYS = ["a", "b", "c", "d"];

/**
 * QuadCollage
 * Renders 4 tiles:
 *  - image src:   {endpoint}/media/{storeId}/about/{1..4}.{ext}
 *  - image link:  {endpoint}/media/{storeId}/about/{1..4}   (extension-less)
 *
 * Props:
 *  - storeId: string (required)
 *  - endpoint: string (default process.env.REACT_APP_API_ENDPOINT)
 *  - ext: "png" | "jpg" | "jpeg" | "webp" | "avif" (default "")
 *  - variant: "grid" | "spotlight-left" | "spotlight-right" | "magazine" | "diamond" | "mosaic" (default "spotlight-left")
 *  - radius: number px (default 5)
 *  - gap: number px (default 1)
 *  - height: CSS height (e.g., "40vh")  — ignored if `ratio` provided
 *  - ratio: CSS aspect-ratio (e.g., "16/9" or 1) — preferred over height
 *  - showIndexBadge: boolean (default false)
 *  - captions: string[4] (optional) — small label bottom-left on hover
 *  - onTileClick: (index: 0..3) => void (optional)
 */
export default function QuadCollage({
  storeId,
  endpoint = process.env.REACT_APP_API_ENDPOINT,
  ext = "",
  folder = "about",
  variant = "spotlight-left",
  radius = 5,
  gap = 1,
  height = "40vh",
  ratio, // e.g. "16/9"
  showIndexBadge = false,
  captions, // array of 4 strings
  onTileClick,
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const layout = VARIANTS[variant] || VARIANTS.grid;
  const base = `${endpoint.replace(/\/+$/, "")}/media/${storeId}/${folder}`;
  const extPart = ext ? `.${ext}` : "";

  // Build the 4 tiles (1..4)
  const tiles = [1, 2, 3, 4].map((n) => ({
    href: `${base}/${n}`, // extension-less for direct link
    src: `${base}/${n}${extPart}`, // image with extension
    alt: `about-${n}`,
    caption: captions?.[n - 1],
    index: n,
  }));

  const handleTileClick = useCallback(
    (i) => {
      setCurrentIndex(i);
      setViewerOpen(true);
      if (onTileClick) onTileClick(i);
    },
    [onTileClick]
  );

  const containerSx = {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: `${radius}px`,
    boxShadow:
      "0 1px 2px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.08)",
    border: "1px solid",
    borderColor: "divider",
    ...(ratio ? { aspectRatio: String(ratio) } : { height }),
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.06))",
  };

  const gridSx = {
    display: "grid",
    width: "100%",
    height: "100%",
    gap: { xs: gap * 0.8, sm: gap, md: gap },
    gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
    gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
    gridTemplateAreas: layout.areas
      .map((row) => `"${row.join(" ")}"`)
      .join(" "),
  };

  return (
    <>
      <Box sx={containerSx}>
        <Box sx={gridSx}>
          {TILE_KEYS.map((key, i) => {
            const t = tiles[i];
            return (
              <Tile
                key={key}
                area={key}
                radius={radius}
                href={t.href}
                src={t.src}
                alt={t.alt}
                index={t.index}
                caption={t.caption}
                showIndexBadge={showIndexBadge}
                onClick={() => handleTileClick(i)}
              />
            );
          })}
        </Box>
      </Box>

      {viewerOpen && (
        <FullscreenViewer
          tiles={tiles}
          currentIndex={currentIndex}
          onClose={() => setViewerOpen(false)}
          onIndexChange={setCurrentIndex}
        />
      )}
    </>
  );
}

/** Individual tile with polished hover/focus styles */
function Tile({
  area,
  radius,
  href,
  src,
  alt,
  index,
  caption,
  showIndexBadge,
  onClick,
}) {
  const handleClick = (e) => {
    // use lightbox instead of opening new tab
    e.preventDefault();
    if (onClick) onClick(e);
  };

  return (
    <Box
      component="a"
      href={href}
      aria-label={alt}
      onClick={handleClick}
      sx={{
        position: "relative",
        gridArea: area,
        borderRadius: `${radius}px`,
        overflow: "hidden",
        outline: "none",
        display: "block",
        userSelect: "none",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:focus-visible": {
          boxShadow:
            "0 0 0 3px rgba(59,130,246,0.6), 0 8px 24px rgba(0,0,0,0.18)",
        },
        "&:hover .img": { transform: "scale(1.06)" },
        "&:hover .overlay": { opacity: 1 },
        "&:hover .cap": { transform: "translateY(0)", opacity: 1 },
        cursor: "pointer",
      }}
    >
      {/* Image */}
      <Box
        className="img"
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.35s ease",
          transform: "scale(1.02)",
          willChange: "transform",
        }}
      />

      {/* Gloss & edge gradient overlay */}
      <Box
        className="overlay"
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          opacity: 0,
          transition: "opacity 0.3s ease",
          background:
            "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.0) 45%), " +
            "linear-gradient(120deg, rgba(255,255,255,0.18), rgba(255,255,255,0.0) 35%)",
          mixBlendMode: "normal",
        }}
      />

      {/* Optional index badge */}
      {showIndexBadge && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            px: 1,
            py: 0.25,
            fontSize: 12,
            fontWeight: 700,
            color: "#fff",
            background: "rgba(0,0,0,0.55)",
            borderRadius: "999px",
            letterSpacing: 0.4,
            lineHeight: 1.2,
          }}
        >
          {index}
        </Box>
      )}

      {/* Optional caption (slides in) */}
      {caption && (
        <Box
          className="cap"
          sx={{
            position: "absolute",
            left: 12,
            bottom: 10,
            pr: 10,
            transform: "translateY(8px)",
            opacity: 0,
            transition: "transform 0.25s ease, opacity 0.25s ease",
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
          >
            {caption}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** Fullscreen lightbox viewer with left/right navigation */
function FullscreenViewer({ tiles, currentIndex, onClose, onIndexChange }) {
  const total = tiles.length;

  const goNext = useCallback(() => {
    onIndexChange((prev) => (typeof prev === "number" ? (prev + 1) % total : 0));
  }, [onIndexChange, total]);

  const goPrev = useCallback(() => {
    onIndexChange((prev) =>
      typeof prev === "number" ? (prev - 1 + total) % total : 0
    );
  }, [onIndexChange, total]);

  // Keyboard navigation: Esc, ←, →
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev, onClose]);

  // Simple swipe support for mobile
  const touch = React.useRef({ x: 0, y: 0 });

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touch.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touch.current.x;
    const threshold = 40; // px
    if (dx > threshold) {
      // swipe right -> previous
      goPrev();
    } else if (dx < -threshold) {
      // swipe left -> next
      goNext();
    }
  };

  const current = tiles[currentIndex];

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        bgcolor: "rgba(0,0,0,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Stop closing when clicking on content */}
      <Box
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{
          position: "relative",
          maxWidth: "96vw",
          maxHeight: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon sx={{ color: "#fff" }} />
        </IconButton>

        {/* Left arrow */}
        {total > 1 && (
          <IconButton
            onClick={goPrev}
            sx={{
              position: "absolute",
              left: 8,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <ChevronLeftIcon sx={{ color: "#fff" }} />
          </IconButton>
        )}

        {/* Image */}
        <Box
          component="img"
          src={current.src}
          alt={current.alt}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: 2,
            boxShadow: "0 10px 40px rgba(0,0,0,0.7)",
          }}
        />

        {/* Right arrow */}
        {total > 1 && (
          <IconButton
            onClick={goNext}
            sx={{
              position: "absolute",
              right: 8,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <ChevronRightIcon sx={{ color: "#fff" }} />
          </IconButton>
        )}

        {/* Index / caption */}
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: 0,
            right: 0,
            textAlign: "center",
            px: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#fff", opacity: 0.85 }}
          >
            {current.caption || `${current.index} / ${total}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
